/**
 * Pure Geometry Generators
 *
 * Renderer-agnostic mesh generation for IVM wireframe shapes.
 * Each generator returns { vertices, edges, metadata } — no rendering code.
 * Used by both Canvas2D playground components and react-pdf SVG renderer.
 */

import type { Vec3 } from './utils-3d';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface WireframeMesh {
  vertices: Vec3[];
  edges: [number, number][];
  metadata: {
    name: string;
    vertexCount: number;
    edgeCount: number;
    faceCount: number;
    formula?: string;
  };
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

export function normalize(v: Vec3): Vec3 {
  const len = Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]);
  if (len === 0) return [0, 0, 0];
  return [v[0] / len, v[1] / len, v[2] / len];
}

export function midpoint(a: Vec3, b: Vec3): Vec3 {
  return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2];
}

export function edgeKey(a: number, b: number): string {
  return a < b ? `${a}-${b}` : `${b}-${a}`;
}

function scale(v: Vec3, s: number): Vec3 {
  return [v[0] * s, v[1] * s, v[2] * s];
}

function addEdgeUnique(
  edges: [number, number][],
  edgeSet: Set<string>,
  a: number,
  b: number,
): void {
  const key = edgeKey(a, b);
  if (!edgeSet.has(key)) {
    edgeSet.add(key);
    edges.push([a, b]);
  }
}

// ---------------------------------------------------------------------------
// 1. Octahedron
// ---------------------------------------------------------------------------

export interface OctahedronOpts {
  radius?: number;
}

export function generateOctahedronMesh(opts: OctahedronOpts = {}): WireframeMesh {
  const r = opts.radius ?? 1;

  const vertices: Vec3[] = [
    [0, r, 0],   // top
    [0, -r, 0],  // bottom
    [r, 0, 0],   // +x
    [-r, 0, 0],  // -x
    [0, 0, r],   // +z
    [0, 0, -r],  // -z
  ];

  const edges: [number, number][] = [
    // Top to equator
    [0, 2], [0, 3], [0, 4], [0, 5],
    // Bottom to equator
    [1, 2], [1, 3], [1, 4], [1, 5],
    // Equator ring
    [2, 4], [4, 3], [3, 5], [5, 2],
  ];

  return {
    vertices,
    edges,
    metadata: {
      name: 'Octahedron',
      vertexCount: 6,
      edgeCount: 12,
      faceCount: 8,
      formula: 'V=6 E=12 F=8',
    },
  };
}

// ---------------------------------------------------------------------------
// 2. Icosahedron
// ---------------------------------------------------------------------------

export interface IcosahedronOpts {
  radius?: number;
}

export function generateIcosahedronMesh(opts: IcosahedronOpts = {}): WireframeMesh {
  const r = opts.radius ?? 1;
  const phi = (1 + Math.sqrt(5)) / 2;

  const rawVerts: Vec3[] = [
    [-1, phi, 0],
    [1, phi, 0],
    [-1, -phi, 0],
    [1, -phi, 0],
    [0, -1, phi],
    [0, 1, phi],
    [0, -1, -phi],
    [0, 1, -phi],
    [phi, 0, -1],
    [phi, 0, 1],
    [-phi, 0, -1],
    [-phi, 0, 1],
  ];

  const vertices: Vec3[] = rawVerts.map((v) => scale(normalize(v), r));

  const faces: [number, number, number][] = [
    [0, 11, 5], [0, 5, 1], [0, 1, 7], [0, 7, 10], [0, 10, 11],
    [1, 5, 9], [5, 11, 4], [11, 10, 2], [10, 7, 6], [7, 1, 8],
    [3, 9, 4], [3, 4, 2], [3, 2, 6], [3, 6, 8], [3, 8, 9],
    [4, 9, 5], [2, 4, 11], [6, 2, 10], [8, 6, 7], [9, 8, 1],
  ];

  const edgeSet = new Set<string>();
  const edges: [number, number][] = [];
  for (const [a, b, c] of faces) {
    addEdgeUnique(edges, edgeSet, a, b);
    addEdgeUnique(edges, edgeSet, b, c);
    addEdgeUnique(edges, edgeSet, a, c);
  }

  return {
    vertices,
    edges,
    metadata: {
      name: 'Icosahedron',
      vertexCount: 12,
      edgeCount: 30,
      faceCount: 20,
      formula: 'V=12 E=30 F=20',
    },
  };
}

// ---------------------------------------------------------------------------
// 3. Vector Equilibrium (Cuboctahedron)
// ---------------------------------------------------------------------------

export interface VectorEquilibriumOpts {
  radius?: number;
}

export function generateVectorEquilibriumMesh(opts: VectorEquilibriumOpts = {}): WireframeMesh {
  const r = opts.radius ?? 1;

  // Cuboctahedron: 12 vertices at midpoints of cube edges
  // A cube with vertices at (±1, ±1, ±1) — midpoints of its 12 edges
  // are the permutations of (0, ±1, ±1)
  const rawVerts: Vec3[] = [
    [0, 1, 1], [0, 1, -1], [0, -1, 1], [0, -1, -1],
    [1, 0, 1], [1, 0, -1], [-1, 0, 1], [-1, 0, -1],
    [1, 1, 0], [-1, 1, 0], [1, -1, 0], [-1, -1, 0],
  ];

  // Normalize to unit sphere then scale
  const vertices: Vec3[] = rawVerts.map((v) => scale(normalize(v), r));

  // The cuboctahedron has 24 edges
  // Each vertex connects to 4 neighbors at equal angular distance
  const faces: [number, number, number][] = [
    // 8 triangular faces
    [0, 4, 8], [0, 8, 1], [1, 5, 8], [4, 10, 8],
    [0, 6, 9], [0, 9, 1], [1, 7, 9], [6, 11, 9],
    [2, 4, 10], [2, 6, 11], [3, 5, 10], [3, 7, 11],
    [2, 4, 6], [3, 5, 7], [8, 5, 10], [9, 7, 11],
  ];

  // Build edges from adjacency — cuboctahedron edges connect vertices
  // that are distance sqrt(2) apart in the raw coordinates
  const edgeSet = new Set<string>();
  const edges: [number, number][] = [];

  for (let i = 0; i < rawVerts.length; i++) {
    for (let j = i + 1; j < rawVerts.length; j++) {
      const dx = rawVerts[i][0] - rawVerts[j][0];
      const dy = rawVerts[i][1] - rawVerts[j][1];
      const dz = rawVerts[i][2] - rawVerts[j][2];
      const dist2 = dx * dx + dy * dy + dz * dz;
      // Adjacent vertices in cuboctahedron are at distance sqrt(2) ≈ dist²=2
      if (Math.abs(dist2 - 2) < 0.01) {
        addEdgeUnique(edges, edgeSet, i, j);
      }
    }
  }

  return {
    vertices,
    edges,
    metadata: {
      name: 'Vector Equilibrium',
      vertexCount: 12,
      edgeCount: edges.length,
      faceCount: 14,
      formula: 'V=12 E=24 F=14 (8△ + 6□)',
    },
  };
}

// ---------------------------------------------------------------------------
// 4. Geodesic Dome (extracted from fuller-dome.tsx)
// ---------------------------------------------------------------------------

export interface GeodesicOpts {
  frequency?: number;
  radius?: number;
  cutHeight?: number;
}

export function generateGeodesicMesh(opts: GeodesicOpts = {}): WireframeMesh {
  const freq = Math.max(1, Math.min(6, Math.round(opts.frequency ?? 3)));
  const radius = opts.radius ?? 2;
  const cutHeight = opts.cutHeight ?? 0;
  const phi = (1 + Math.sqrt(5)) / 2;

  // 12 icosahedron base vertices
  const icoVerts: Vec3[] = (
    [
      [-1, phi, 0], [1, phi, 0], [-1, -phi, 0], [1, -phi, 0],
      [0, -1, phi], [0, 1, phi], [0, -1, -phi], [0, 1, -phi],
      [phi, 0, -1], [phi, 0, 1], [-phi, 0, -1], [-phi, 0, 1],
    ] as Vec3[]
  ).map(normalize);

  const faces: [number, number, number][] = [
    [0, 11, 5], [0, 5, 1], [0, 1, 7], [0, 7, 10], [0, 10, 11],
    [1, 5, 9], [5, 11, 4], [11, 10, 2], [10, 7, 6], [7, 1, 8],
    [3, 9, 4], [3, 4, 2], [3, 2, 6], [3, 6, 8], [3, 8, 9],
    [4, 9, 5], [2, 4, 11], [6, 2, 10], [8, 6, 7], [9, 8, 1],
  ];

  // Subdivide
  const vertexMap = new Map<string, number>();
  const vertices: Vec3[] = [];
  const triangles: [number, number, number][] = [];

  function getVertexIndex(v: Vec3): number {
    const nv = normalize(v);
    const key = `${nv[0].toFixed(6)},${nv[1].toFixed(6)},${nv[2].toFixed(6)}`;
    const existing = vertexMap.get(key);
    if (existing !== undefined) return existing;
    const idx = vertices.length;
    vertices.push(nv);
    vertexMap.set(key, idx);
    return idx;
  }

  for (const [ai, bi, ci] of faces) {
    const A = icoVerts[ai];
    const B = icoVerts[bi];
    const C = icoVerts[ci];

    const grid: number[][] = [];
    for (let i = 0; i <= freq; i++) {
      const row: number[] = [];
      for (let j = 0; j <= freq - i; j++) {
        const u = i / freq;
        const v = j / freq;
        const w = 1 - u - v;
        const p: Vec3 = [
          A[0] * w + B[0] * u + C[0] * v,
          A[1] * w + B[1] * u + C[1] * v,
          A[2] * w + B[2] * u + C[2] * v,
        ];
        row.push(getVertexIndex(p));
      }
      grid.push(row);
    }

    for (let i = 0; i < freq; i++) {
      for (let j = 0; j < freq - i; j++) {
        triangles.push([grid[i][j], grid[i + 1][j], grid[i][j + 1]]);
        if (j + 1 <= freq - i - 1) {
          triangles.push([grid[i + 1][j], grid[i + 1][j + 1], grid[i][j + 1]]);
        }
      }
    }
  }

  // Scale
  const scaledVerts: Vec3[] = vertices.map((v) => scale(v, radius));

  // Collect all edges
  const allEdgeSet = new Set<string>();
  const allEdges: [number, number][] = [];
  for (const [a, b, c] of triangles) {
    addEdgeUnique(allEdges, allEdgeSet, a, b);
    addEdgeUnique(allEdges, allEdgeSet, b, c);
    addEdgeUnique(allEdges, allEdgeSet, a, c);
  }

  // Skip clipping entirely for full sphere (cutHeight at or below -radius)
  if (cutHeight <= -radius) {
    const usedVerts = new Set<number>();
    for (const [a, b] of allEdges) {
      usedVerts.add(a);
      usedVerts.add(b);
    }
    return {
      vertices: scaledVerts,
      edges: allEdges,
      metadata: {
        name: 'Geodesic Sphere',
        vertexCount: usedVerts.size,
        edgeCount: allEdges.length,
        faceCount: triangles.length,
        formula: `freq=${freq} r=${radius}`,
      },
    };
  }

  // Clip at cutHeight
  const clippedVerts = [...scaledVerts];
  const clippedEdges: [number, number][] = [];
  let faceCount = 0;

  for (const [a, b] of allEdges) {
    const va = scaledVerts[a];
    const vb = scaledVerts[b];
    const aAbove = va[1] >= cutHeight;
    const bAbove = vb[1] >= cutHeight;

    if (aAbove && bAbove) {
      clippedEdges.push([a, b]);
    } else if (!aAbove && !bAbove) {
      continue;
    } else {
      const t = (cutHeight - va[1]) / (vb[1] - va[1]);
      const clipPoint: Vec3 = [
        va[0] + t * (vb[0] - va[0]),
        cutHeight,
        va[2] + t * (vb[2] - va[2]),
      ];
      const clipIdx = clippedVerts.length;
      clippedVerts.push(clipPoint);
      clippedEdges.push(aAbove ? [a, clipIdx] : [clipIdx, b]);
    }
  }

  for (const [a, b, c] of triangles) {
    if (scaledVerts[a][1] >= cutHeight && scaledVerts[b][1] >= cutHeight && scaledVerts[c][1] >= cutHeight) {
      faceCount++;
    }
  }

  const usedVerts = new Set<number>();
  for (const [a, b] of clippedEdges) {
    usedVerts.add(a);
    usedVerts.add(b);
  }

  return {
    vertices: clippedVerts,
    edges: clippedEdges,
    metadata: {
      name: 'Geodesic Dome',
      vertexCount: usedVerts.size,
      edgeCount: clippedEdges.length,
      faceCount,
      formula: `freq=${freq} r=${radius}`,
    },
  };
}

// ---------------------------------------------------------------------------
// 5. Torus (extracted from torus-wireframe.tsx)
// ---------------------------------------------------------------------------

const TAU = Math.PI * 2;

export interface TorusOpts {
  segments?: number;
  tubes?: number;
  majorRadius?: number;
  minorRadius?: number;
}

export function generateTorusMesh(opts: TorusOpts = {}): WireframeMesh {
  const segments = opts.segments ?? 40;
  const tubes = opts.tubes ?? 24;
  const R = opts.majorRadius ?? 1.6;
  const r = opts.minorRadius ?? 0.6;

  // Build vertex grid
  const vertices: Vec3[] = [];
  const grid: number[][] = [];

  for (let i = 0; i <= segments; i++) {
    const row: number[] = [];
    const u = (i / segments) * TAU;
    const cosU = Math.cos(u);
    const sinU = Math.sin(u);

    for (let j = 0; j <= tubes; j++) {
      const v = (j / tubes) * TAU;
      const cosV = Math.cos(v);
      const sinV = Math.sin(v);
      row.push(vertices.length);
      vertices.push([
        (R + r * cosV) * cosU,
        (R + r * cosV) * sinU,
        r * sinV,
      ]);
    }
    grid.push(row);
  }

  const edges: [number, number][] = [];

  // Longitudinal edges (along tubes)
  for (let i = 0; i < segments; i++) {
    for (let j = 0; j < tubes; j++) {
      edges.push([grid[i][j], grid[i][j + 1]]);
    }
  }

  // Latitudinal edges (along segments)
  for (let i = 0; i < segments; i++) {
    for (let j = 0; j <= tubes; j++) {
      edges.push([grid[i][j], grid[i + 1][j]]);
    }
  }

  // Diagonal edges (IVM triangulation)
  for (let i = 0; i < segments; i++) {
    for (let j = 0; j < tubes; j++) {
      if ((i + j) % 2 === 0) {
        edges.push([grid[i][j], grid[i + 1][j + 1]]);
      } else {
        edges.push([grid[i][j + 1], grid[i + 1][j]]);
      }
    }
  }

  const vertexCount = (segments + 1) * (tubes + 1);
  const horizEdges = segments * tubes;
  const vertEdges = segments * (tubes + 1);
  const diagEdges = segments * tubes;

  return {
    vertices,
    edges,
    metadata: {
      name: 'Torus',
      vertexCount,
      edgeCount: horizEdges + vertEdges + diagEdges,
      faceCount: segments * tubes * 2,
      formula: `f(u,v) = ((R+r·cos v)·cos u, (R+r·cos v)·sin u, r·sin v)`,
    },
  };
}

// ---------------------------------------------------------------------------
// 6. Triangle (equilateral with optional subdivision)
// ---------------------------------------------------------------------------

export interface TriangleOpts {
  radius?: number;
  subdivisions?: number;
}

export function generateTriangleMesh(opts: TriangleOpts = {}): WireframeMesh {
  const r = opts.radius ?? 1;
  const subdivisions = Math.max(0, Math.min(3, opts.subdivisions ?? 0));

  // Equilateral triangle in XY plane, centered at origin (circumradius = r)
  let vertices: Vec3[] = [
    [0, r, 0],
    [-r * Math.sqrt(3) / 2, -r / 2, 0],
    [r * Math.sqrt(3) / 2, -r / 2, 0],
  ];

  let triangles: [number, number, number][] = [[0, 1, 2]];

  // Subdivide
  for (let s = 0; s < subdivisions; s++) {
    const newVerts = [...vertices];
    const newTris: [number, number, number][] = [];
    const midCache = new Map<string, number>();

    const getMid = (a: number, b: number): number => {
      const key = edgeKey(a, b);
      const cached = midCache.get(key);
      if (cached !== undefined) return cached;
      const idx = newVerts.length;
      newVerts.push(midpoint(newVerts[a], newVerts[b]));
      midCache.set(key, idx);
      return idx;
    };

    for (const [a, b, c] of triangles) {
      const ab = getMid(a, b);
      const bc = getMid(b, c);
      const ca = getMid(c, a);
      newTris.push([a, ab, ca]);
      newTris.push([ab, b, bc]);
      newTris.push([ca, bc, c]);
      newTris.push([ab, bc, ca]);
    }

    vertices = newVerts;
    triangles = newTris;
  }

  const edgeSet = new Set<string>();
  const edges: [number, number][] = [];
  for (const [a, b, c] of triangles) {
    addEdgeUnique(edges, edgeSet, a, b);
    addEdgeUnique(edges, edgeSet, b, c);
    addEdgeUnique(edges, edgeSet, a, c);
  }

  return {
    vertices,
    edges,
    metadata: {
      name: 'Triangle',
      vertexCount: vertices.length,
      edgeCount: edges.length,
      faceCount: triangles.length,
      formula: `subdiv=${subdivisions}`,
    },
  };
}

// ---------------------------------------------------------------------------
// 7. Graphene (flat hexagonal lattice with slight 3D tilt)
// ---------------------------------------------------------------------------

export interface GrapheneOpts {
  radius?: number;
  /** Number of hexagon rings from center */
  rings?: number;
  /** Slight Z tilt for 3D effect */
  tilt?: number;
}

export function generateGrapheneMesh(opts: GrapheneOpts = {}): WireframeMesh {
  const bondLen = opts.radius ?? 0.3;
  const rings = opts.rings ?? 3;
  const tilt = opts.tilt ?? 0.05;

  const vertices: Vec3[] = [];
  const vertexMap = new Map<string, number>();
  const edgeSet = new Set<string>();
  const edges: [number, number][] = [];

  function vertKey(x: number, y: number): string {
    // Round-trip through toFixed→Number→toFixed to eliminate "-0.0000"
    // from tiny negative floats (e.g. -5.5e-17 → "-0.0000" vs "0.0000")
    return `${(+x.toFixed(4)).toFixed(4)},${(+y.toFixed(4)).toFixed(4)}`;
  }

  function getVert(x: number, y: number): number {
    const key = vertKey(x, y);
    const existing = vertexMap.get(key);
    if (existing !== undefined) return existing;
    const idx = vertices.length;
    // Add slight z variation based on distance from center for 3D feel
    const dist = Math.sqrt(x * x + y * y);
    const z = dist * tilt;
    vertices.push([x, y, z]);
    vertexMap.set(key, idx);
    return idx;
  }

  // Honeycomb lattice: two offset triangular sublattices
  const sqrt3 = Math.sqrt(3);
  const a1: [number, number] = [sqrt3 * bondLen, 0];
  const a2: [number, number] = [sqrt3 * bondLen / 2, 1.5 * bondLen];

  // Basis atoms within unit cell
  const basis: [number, number][] = [
    [0, 0],
    [sqrt3 * bondLen / 2, bondLen / 2],
  ];

  const maxDist = (rings + 0.5) * sqrt3 * bondLen;

  for (let i = -rings - 1; i <= rings + 1; i++) {
    for (let j = -rings - 1; j <= rings + 1; j++) {
      for (const [bx, by] of basis) {
        const x = i * a1[0] + j * a2[0] + bx;
        const y = i * a1[1] + j * a2[1] + by;
        if (Math.sqrt(x * x + y * y) > maxDist) continue;
        getVert(x, y);
      }
    }
  }

  // Connect neighbors — each atom connects to 3 nearest
  // Bond directions from sublattice A
  const directions: [number, number][] = [
    [sqrt3 * bondLen / 2, bondLen / 2],
    [-sqrt3 * bondLen / 2, bondLen / 2],
    [0, -bondLen],
  ];

  for (let i = -rings - 1; i <= rings + 1; i++) {
    for (let j = -rings - 1; j <= rings + 1; j++) {
      const ax = i * a1[0] + j * a2[0] + basis[0][0];
      const ay = i * a1[1] + j * a2[1] + basis[0][1];
      if (Math.sqrt(ax * ax + ay * ay) > maxDist) continue;

      const aIdx = vertexMap.get(vertKey(ax, ay));
      if (aIdx === undefined) continue;

      for (const [dx, dy] of directions) {
        const bx = ax + dx;
        const by = ay + dy;
        const bIdx = vertexMap.get(vertKey(bx, by));
        if (bIdx !== undefined) {
          addEdgeUnique(edges, edgeSet, aIdx, bIdx);
        }
      }
    }
  }

  return {
    vertices,
    edges,
    metadata: {
      name: 'Graphene',
      vertexCount: vertices.length,
      edgeCount: edges.length,
      faceCount: Math.floor(edges.length / 3), // approximate hex count
      formula: `C₆ hexagonal lattice, rings=${rings}`,
    },
  };
}

// ---------------------------------------------------------------------------
// 8. Tetrahedron
// ---------------------------------------------------------------------------

export interface TetrahedronOpts {
  radius?: number;
}

export function generateTetrahedronMesh(opts: TetrahedronOpts = {}): WireframeMesh {
  const r = opts.radius ?? 1;

  // Regular tetrahedron: vertices of a cube subset, normalized to sphere
  const rawVerts: Vec3[] = [
    [1, 1, 1],
    [1, -1, -1],
    [-1, 1, -1],
    [-1, -1, 1],
  ];

  const vertices: Vec3[] = rawVerts.map((v) => scale(normalize(v), r));

  // All 6 edges (complete graph K4)
  const edges: [number, number][] = [
    [0, 1], [0, 2], [0, 3],
    [1, 2], [1, 3], [2, 3],
  ];

  return {
    vertices,
    edges,
    metadata: {
      name: 'Tetrahedron',
      vertexCount: 4,
      edgeCount: 6,
      faceCount: 4,
      formula: 'V=4 E=6 F=4',
    },
  };
}

// ---------------------------------------------------------------------------
// 9. IVM — Isotropic Vector Matrix (octet truss / FCC nearest-neighbor lattice)
// ---------------------------------------------------------------------------

export interface IvmOpts {
  radius?: number;
  /** Unit cells per axis (default 3) */
  cells?: number;
}

export function generateIvmMesh(opts: IvmOpts = {}): WireframeMesh {
  const r = opts.radius ?? 1.5;
  const cells = opts.cells ?? 3;
  const a = 1; // unit cell size

  const vertices: Vec3[] = [];
  const vertexMap = new Map<string, number>();
  const edgeSet = new Set<string>();
  const edges: [number, number][] = [];

  function getVert(x: number, y: number, z: number): number {
    const key = `${x.toFixed(4)},${y.toFixed(4)},${z.toFixed(4)}`;
    const existing = vertexMap.get(key);
    if (existing !== undefined) return existing;
    const idx = vertices.length;
    vertices.push([x, y, z]);
    vertexMap.set(key, idx);
    return idx;
  }

  // FCC basis within each unit cell
  const basis: Vec3[] = [
    [0, 0, 0],
    [a / 2, a / 2, 0],
    [a / 2, 0, a / 2],
    [0, a / 2, a / 2],
  ];

  // Generate all FCC lattice points
  for (let ci = 0; ci <= cells; ci++) {
    for (let cj = 0; cj <= cells; cj++) {
      for (let ck = 0; ck <= cells; ck++) {
        for (const [bx, by, bz] of basis) {
          getVert(ci * a + bx, cj * a + by, ck * a + bz);
        }
      }
    }
  }

  // FCC nearest-neighbor distance² = a²/2
  const nnDist2 = (a * a) / 2;
  const tolerance = 0.01;

  // Connect all nearest-neighbor pairs → tetrahedra + octahedra
  for (let i = 0; i < vertices.length; i++) {
    for (let j = i + 1; j < vertices.length; j++) {
      const dx = vertices[i][0] - vertices[j][0];
      const dy = vertices[i][1] - vertices[j][1];
      const dz = vertices[i][2] - vertices[j][2];
      const dist2 = dx * dx + dy * dy + dz * dz;
      if (Math.abs(dist2 - nnDist2) < tolerance) {
        addEdgeUnique(edges, edgeSet, i, j);
      }
    }
  }

  // Center and scale to fit radius
  let maxDist = 0;
  let cx = 0, cy = 0, cz = 0;
  for (const v of vertices) {
    cx += v[0]; cy += v[1]; cz += v[2];
  }
  cx /= vertices.length;
  cy /= vertices.length;
  cz /= vertices.length;

  for (const v of vertices) {
    v[0] -= cx;
    v[1] -= cy;
    v[2] -= cz;
    const d = Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]);
    if (d > maxDist) maxDist = d;
  }

  if (maxDist > 0) {
    const s = r / maxDist;
    for (const v of vertices) {
      v[0] *= s;
      v[1] *= s;
      v[2] *= s;
    }
  }

  return {
    vertices,
    edges,
    metadata: {
      name: 'Isotropic Vector Matrix',
      vertexCount: vertices.length,
      edgeCount: edges.length,
      faceCount: 0,
      formula: `FCC octet truss, ${cells}³ cells`,
    },
  };
}

// ---------------------------------------------------------------------------
// 10. Cube (Hexahedron)
// ---------------------------------------------------------------------------

export interface CubeOpts {
  radius?: number;
}

export function generateCubeMesh(opts: CubeOpts = {}): WireframeMesh {
  const r = opts.radius ?? 1;
  const s = r / Math.sqrt(3); // inscribed in sphere of radius r

  const vertices: Vec3[] = [
    [-s, -s, -s], [s, -s, -s], [s, s, -s], [-s, s, -s],
    [-s, -s, s], [s, -s, s], [s, s, s], [-s, s, s],
  ];

  const edges: [number, number][] = [
    // bottom face
    [0, 1], [1, 2], [2, 3], [3, 0],
    // top face
    [4, 5], [5, 6], [6, 7], [7, 4],
    // verticals
    [0, 4], [1, 5], [2, 6], [3, 7],
  ];

  return {
    vertices,
    edges,
    metadata: {
      name: 'Cube',
      vertexCount: 8,
      edgeCount: 12,
      faceCount: 6,
      formula: 'V=8 E=12 F=6',
    },
  };
}

// ---------------------------------------------------------------------------
// 11. Dodecahedron
// ---------------------------------------------------------------------------

export interface DodecahedronOpts {
  radius?: number;
}

export function generateDodecahedronMesh(opts: DodecahedronOpts = {}): WireframeMesh {
  const r = opts.radius ?? 1;
  const phi = (1 + Math.sqrt(5)) / 2;
  const invPhi = 1 / phi;

  // 20 vertices: permutations of (±1,±1,±1), (0,±invPhi,±phi), (±invPhi,±phi,0), (±phi,0,±invPhi)
  const rawVerts: Vec3[] = [];

  // 8 cube vertices
  for (const sx of [-1, 1]) {
    for (const sy of [-1, 1]) {
      for (const sz of [-1, 1]) {
        rawVerts.push([sx, sy, sz]);
      }
    }
  }

  // 12 vertices from three sets of rectangles
  for (const s1 of [-1, 1]) {
    for (const s2 of [-1, 1]) {
      rawVerts.push([0, s1 * invPhi, s2 * phi]);
      rawVerts.push([s1 * invPhi, s2 * phi, 0]);
      rawVerts.push([s1 * phi, 0, s2 * invPhi]);
    }
  }

  const vertices: Vec3[] = rawVerts.map((v) => scale(normalize(v), r));

  // Connect vertices at the dodecahedron edge distance
  // In these coordinates, edge length = 2/phi
  const edgeDist2 = (2 / phi) * (2 / phi);
  const tolerance = 0.01;
  const edgeSet = new Set<string>();
  const edges: [number, number][] = [];

  for (let i = 0; i < rawVerts.length; i++) {
    for (let j = i + 1; j < rawVerts.length; j++) {
      const dx = rawVerts[i][0] - rawVerts[j][0];
      const dy = rawVerts[i][1] - rawVerts[j][1];
      const dz = rawVerts[i][2] - rawVerts[j][2];
      const dist2 = dx * dx + dy * dy + dz * dz;
      if (Math.abs(dist2 - edgeDist2) < tolerance) {
        addEdgeUnique(edges, edgeSet, i, j);
      }
    }
  }

  return {
    vertices,
    edges,
    metadata: {
      name: 'Dodecahedron',
      vertexCount: 20,
      edgeCount: 30,
      faceCount: 12,
      formula: 'V=20 E=30 F=12',
    },
  };
}

// ---------------------------------------------------------------------------
// 12. Star Tetrahedron (Merkaba) — two interlocking tetrahedra
// ---------------------------------------------------------------------------

export interface StarTetrahedronOpts {
  radius?: number;
}

export function generateStarTetrahedronMesh(opts: StarTetrahedronOpts = {}): WireframeMesh {
  const r = opts.radius ?? 1;

  // Tetrahedron A — vertices of a cube subset
  const rawA: Vec3[] = [
    [1, 1, 1], [1, -1, -1], [-1, 1, -1], [-1, -1, 1],
  ];
  // Tetrahedron B — the complementary subset (inverted)
  const rawB: Vec3[] = [
    [-1, -1, -1], [-1, 1, 1], [1, -1, 1], [1, 1, -1],
  ];

  const allRaw = [...rawA, ...rawB];
  const vertices: Vec3[] = allRaw.map((v) => scale(normalize(v), r));

  // 6 edges per tetrahedron = 12 total
  const edges: [number, number][] = [
    // Tetrahedron A (indices 0-3)
    [0, 1], [0, 2], [0, 3], [1, 2], [1, 3], [2, 3],
    // Tetrahedron B (indices 4-7)
    [4, 5], [4, 6], [4, 7], [5, 6], [5, 7], [6, 7],
  ];

  return {
    vertices,
    edges,
    metadata: {
      name: 'Star Tetrahedron',
      vertexCount: 8,
      edgeCount: 12,
      faceCount: 8,
      formula: 'Merkaba — 2 × tetrahedra',
    },
  };
}

// ---------------------------------------------------------------------------
// 13. Truncated Icosahedron (Buckyball / C₆₀)
// ---------------------------------------------------------------------------

export interface BuckyballOpts {
  radius?: number;
}

export function generateBuckyballMesh(opts: BuckyballOpts = {}): WireframeMesh {
  const r = opts.radius ?? 1;
  const phi = (1 + Math.sqrt(5)) / 2;

  // 60 vertices from even permutations of three seed types with all sign combos
  const seeds: Vec3[] = [
    [0, 1, 3 * phi],
    [1, 2 + phi, 2 * phi],
    [2, 1 + 2 * phi, phi],
  ];

  const rawVerts: Vec3[] = [];

  for (const [a, b, c] of seeds) {
    // Even (cyclic) permutations: (a,b,c), (c,a,b), (b,c,a)
    const perms: Vec3[] = [[a, b, c], [c, a, b], [b, c, a]];
    for (const [x, y, z] of perms) {
      // All sign combinations, but skip zero-signs for non-zero coords
      const xs = x === 0 ? [0] : [-x, x];
      const ys = y === 0 ? [0] : [-y, y];
      const zs = z === 0 ? [0] : [-z, z];
      for (const sx of xs) {
        for (const sy of ys) {
          for (const sz of zs) {
            rawVerts.push([sx, sy, sz]);
          }
        }
      }
    }
  }

  // Deduplicate (shouldn't have dupes, but be safe)
  const vertMap = new Map<string, number>();
  const uniqueVerts: Vec3[] = [];
  for (const v of rawVerts) {
    const key = `${v[0].toFixed(4)},${v[1].toFixed(4)},${v[2].toFixed(4)}`;
    if (!vertMap.has(key)) {
      vertMap.set(key, uniqueVerts.length);
      uniqueVerts.push(v);
    }
  }

  // Find nearest-neighbor distance (edge length)
  let minDist2 = Infinity;
  for (let i = 0; i < uniqueVerts.length; i++) {
    for (let j = i + 1; j < uniqueVerts.length; j++) {
      const dx = uniqueVerts[i][0] - uniqueVerts[j][0];
      const dy = uniqueVerts[i][1] - uniqueVerts[j][1];
      const dz = uniqueVerts[i][2] - uniqueVerts[j][2];
      const d2 = dx * dx + dy * dy + dz * dz;
      if (d2 > 0.01 && d2 < minDist2) minDist2 = d2;
    }
  }

  // Connect all pairs at the edge distance
  const tolerance = 0.1;
  const edgeSet = new Set<string>();
  const edges: [number, number][] = [];
  for (let i = 0; i < uniqueVerts.length; i++) {
    for (let j = i + 1; j < uniqueVerts.length; j++) {
      const dx = uniqueVerts[i][0] - uniqueVerts[j][0];
      const dy = uniqueVerts[i][1] - uniqueVerts[j][1];
      const dz = uniqueVerts[i][2] - uniqueVerts[j][2];
      const d2 = dx * dx + dy * dy + dz * dz;
      if (Math.abs(d2 - minDist2) < tolerance) {
        addEdgeUnique(edges, edgeSet, i, j);
      }
    }
  }

  // Normalize and scale
  const vertices: Vec3[] = uniqueVerts.map((v) => scale(normalize(v), r));

  return {
    vertices,
    edges,
    metadata: {
      name: 'Buckyball',
      vertexCount: vertices.length,
      edgeCount: edges.length,
      faceCount: 32,
      formula: 'C₆₀ — V=60 E=90 F=32 (12⬠ + 20⬡)',
    },
  };
}

// ---------------------------------------------------------------------------
// 14. Truncated Octahedron (Archimedean solid, space-filling)
// ---------------------------------------------------------------------------

export interface TruncatedOctahedronOpts {
  radius?: number;
}

export function generateTruncatedOctahedronMesh(opts: TruncatedOctahedronOpts = {}): WireframeMesh {
  const r = opts.radius ?? 1;

  // 24 vertices: all permutations of (0, ±1, ±2)
  const rawVerts: Vec3[] = [];
  for (const s1 of [-1, 1]) {
    for (const s2 of [-1, 1]) {
      rawVerts.push([0, s1 * 1, s2 * 2]);
      rawVerts.push([0, s1 * 2, s2 * 1]);
      rawVerts.push([s1 * 1, 0, s2 * 2]);
      rawVerts.push([s1 * 2, 0, s2 * 1]);
      rawVerts.push([s1 * 1, s2 * 2, 0]);
      rawVerts.push([s1 * 2, s2 * 1, 0]);
    }
  }

  const vertices: Vec3[] = rawVerts.map((v) => scale(normalize(v), r));

  // Edge length² = 2 in raw coordinates
  const edgeDist2 = 2;
  const tolerance = 0.01;
  const edgeSet = new Set<string>();
  const edges: [number, number][] = [];

  for (let i = 0; i < rawVerts.length; i++) {
    for (let j = i + 1; j < rawVerts.length; j++) {
      const dx = rawVerts[i][0] - rawVerts[j][0];
      const dy = rawVerts[i][1] - rawVerts[j][1];
      const dz = rawVerts[i][2] - rawVerts[j][2];
      const dist2 = dx * dx + dy * dy + dz * dz;
      if (Math.abs(dist2 - edgeDist2) < tolerance) {
        addEdgeUnique(edges, edgeSet, i, j);
      }
    }
  }

  return {
    vertices,
    edges,
    metadata: {
      name: 'Truncated Octahedron',
      vertexCount: 24,
      edgeCount: 36,
      faceCount: 14,
      formula: 'V=24 E=36 F=14 (8⬡ + 6□)',
    },
  };
}

// ---------------------------------------------------------------------------
// 15. Trefoil Knot (parametric torus knot tube)
// ---------------------------------------------------------------------------

export interface TrefoilKnotOpts {
  radius?: number;
  tubeRadius?: number;
  segments?: number;
  tubeSegments?: number;
}

export function generateTrefoilKnotMesh(opts: TrefoilKnotOpts = {}): WireframeMesh {
  const R = opts.radius ?? 1.4;
  const tubeR = opts.tubeRadius ?? 0.35;
  const segments = opts.segments ?? 120;
  const tubeSeg = opts.tubeSegments ?? 8;

  const vertices: Vec3[] = [];
  const edges: [number, number][] = [];

  // Torus knot parametric: p=2, q=3 (trefoil)
  function knotPoint(t: number): Vec3 {
    const r2 = Math.cos(3 * t) + 2;
    return [
      r2 * Math.cos(2 * t) * R * 0.4,
      r2 * Math.sin(2 * t) * R * 0.4,
      -Math.sin(3 * t) * R * 0.4,
    ];
  }

  function knotTangent(t: number): Vec3 {
    const dt = 0.001;
    const a = knotPoint(t - dt);
    const b = knotPoint(t + dt);
    return normalize([b[0] - a[0], b[1] - a[1], b[2] - a[2]]);
  }

  // Build tube mesh
  for (let i = 0; i < segments; i++) {
    const t = (i / segments) * TAU;
    const center = knotPoint(t);
    const T = knotTangent(t);

    // Compute a consistent normal frame using cross products
    const up: Vec3 = Math.abs(T[1]) < 0.99 ? [0, 1, 0] : [1, 0, 0];
    const N: Vec3 = normalize([
      T[1] * up[2] - T[2] * up[1],
      T[2] * up[0] - T[0] * up[2],
      T[0] * up[1] - T[1] * up[0],
    ]);
    const B: Vec3 = [
      T[1] * N[2] - T[2] * N[1],
      T[2] * N[0] - T[0] * N[2],
      T[0] * N[1] - T[1] * N[0],
    ];

    for (let j = 0; j < tubeSeg; j++) {
      const angle = (j / tubeSeg) * TAU;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      vertices.push([
        center[0] + tubeR * (cos * N[0] + sin * B[0]),
        center[1] + tubeR * (cos * N[1] + sin * B[1]),
        center[2] + tubeR * (cos * N[2] + sin * B[2]),
      ]);
    }
  }

  // Connect tube segments
  for (let i = 0; i < segments; i++) {
    const nextI = (i + 1) % segments;
    for (let j = 0; j < tubeSeg; j++) {
      const nextJ = (j + 1) % tubeSeg;
      const curr = i * tubeSeg + j;
      const currNext = i * tubeSeg + nextJ;
      const next = nextI * tubeSeg + j;

      // Ring edge
      edges.push([curr, currNext]);
      // Longitudinal edge
      edges.push([curr, next]);
    }
  }

  return {
    vertices,
    edges,
    metadata: {
      name: 'Trefoil Knot',
      vertexCount: vertices.length,
      edgeCount: edges.length,
      faceCount: segments * tubeSeg,
      formula: 'Torus knot T(2,3)',
    },
  };
}

// ---------------------------------------------------------------------------
// 16. Möbius Strip (non-orientable surface)
// ---------------------------------------------------------------------------

export interface MobiusStripOpts {
  radius?: number;
  width?: number;
  segments?: number;
  strips?: number;
}

export function generateMobiusStripMesh(opts: MobiusStripOpts = {}): WireframeMesh {
  const R = opts.radius ?? 1.5;
  const W = opts.width ?? 0.6;
  const segments = opts.segments ?? 80;
  const strips = opts.strips ?? 6;

  const vertices: Vec3[] = [];
  const edges: [number, number][] = [];

  // Parametric Möbius strip:
  // x = (R + s*cos(t/2)) * cos(t)
  // y = (R + s*cos(t/2)) * sin(t)
  // z = s * sin(t/2)
  // where t ∈ [0, 2π), s ∈ [-W/2, W/2]

  for (let i = 0; i <= segments; i++) {
    const t = (i / segments) * TAU;
    const halfT = t / 2;
    const cosT = Math.cos(t);
    const sinT = Math.sin(t);
    const cosHalf = Math.cos(halfT);
    const sinHalf = Math.sin(halfT);

    for (let j = 0; j <= strips; j++) {
      const s = -W / 2 + (j / strips) * W;
      vertices.push([
        (R + s * cosHalf) * cosT,
        (R + s * cosHalf) * sinT,
        s * sinHalf,
      ]);
    }
  }

  const cols = strips + 1;

  for (let i = 0; i < segments; i++) {
    for (let j = 0; j < strips; j++) {
      const curr = i * cols + j;
      const currNext = i * cols + j + 1;
      const next = (i + 1) * cols + j;

      // Along strip width
      edges.push([curr, currNext]);
      // Along strip length
      edges.push([curr, next]);
    }
    // Last width edge in this ring
    edges.push([i * cols + strips, (i + 1) * cols + strips]);
  }

  return {
    vertices,
    edges,
    metadata: {
      name: 'Mobius Strip',
      vertexCount: vertices.length,
      edgeCount: edges.length,
      faceCount: segments * strips,
      formula: 'Non-orientable surface, half-twist',
    },
  };
}

// ---------------------------------------------------------------------------
// 17. Pyramid (square base)
// ---------------------------------------------------------------------------

export interface PyramidOpts { radius?: number; height?: number }

export function generatePyramidMesh(opts: PyramidOpts = {}): WireframeMesh {
  const r = opts.radius ?? 1.4;
  const h = opts.height ?? 2;
  const y0 = -h * 0.3;
  const vertices: Vec3[] = [
    [-r, y0, -r], [r, y0, -r], [r, y0, r], [-r, y0, r], // base
    [0, y0 + h, 0], // apex
  ];
  const edges: [number, number][] = [
    [0, 1], [1, 2], [2, 3], [3, 0], // base
    [0, 4], [1, 4], [2, 4], [3, 4], // sides
  ];
  return { vertices, edges, metadata: { name: 'Pyramid', vertexCount: 5, edgeCount: 8, faceCount: 5, formula: 'V=5 E=8 F=5' } };
}

// ---------------------------------------------------------------------------
// 18. Helix (single spiral)
// ---------------------------------------------------------------------------

export interface HelixOpts { radius?: number; pitch?: number; turns?: number; segments?: number }

export function generateHelixMesh(opts: HelixOpts = {}): WireframeMesh {
  const r = opts.radius ?? 1;
  const pitch = opts.pitch ?? 0.6;
  const turns = opts.turns ?? 4;
  const n = opts.segments ?? 120;
  const vertices: Vec3[] = [];
  const edges: [number, number][] = [];
  const totalAngle = turns * TAU;
  const halfH = (turns * pitch) / 2;
  for (let i = 0; i <= n; i++) {
    const t = (i / n) * totalAngle;
    vertices.push([r * Math.cos(t), t * (pitch / TAU) - halfH, r * Math.sin(t)]);
    if (i > 0) edges.push([i - 1, i]);
  }
  return { vertices, edges, metadata: { name: 'Helix', vertexCount: vertices.length, edgeCount: edges.length, faceCount: 0, formula: `${turns} turns, pitch=${pitch}` } };
}

// ---------------------------------------------------------------------------
// 19. Double Helix (DNA-style)
// ---------------------------------------------------------------------------

export interface DoubleHelixOpts { radius?: number; pitch?: number; turns?: number; segments?: number; rungs?: number; tubeSegments?: number }

export function generateDoubleHelixMesh(opts: DoubleHelixOpts = {}): WireframeMesh {
  const r = opts.radius ?? 1.2;
  const pitch = opts.pitch ?? 2.8;
  const turns = opts.turns ?? 1;
  const n = opts.segments ?? 80;
  const rungCount = opts.rungs ?? 10;
  const tubeSeg = opts.tubeSegments ?? 4;
  const tubeR = 0.12;
  const vertices: Vec3[] = [];
  const edges: [number, number][] = [];
  const totalAngle = turns * TAU;
  const halfH = (turns * pitch) / 2;

  // Build a tube ring around a center point along a direction
  function addTubeRing(center: Vec3, tangent: Vec3): number {
    const base = vertices.length;
    const up: Vec3 = Math.abs(tangent[1]) < 0.99 ? [0, 1, 0] : [1, 0, 0];
    const N: Vec3 = normalize([
      tangent[1] * up[2] - tangent[2] * up[1],
      tangent[2] * up[0] - tangent[0] * up[2],
      tangent[0] * up[1] - tangent[1] * up[0],
    ]);
    const B: Vec3 = [
      tangent[1] * N[2] - tangent[2] * N[1],
      tangent[2] * N[0] - tangent[0] * N[2],
      tangent[0] * N[1] - tangent[1] * N[0],
    ];
    for (let j = 0; j < tubeSeg; j++) {
      const a = (j / tubeSeg) * TAU;
      vertices.push([
        center[0] + tubeR * (Math.cos(a) * N[0] + Math.sin(a) * B[0]),
        center[1] + tubeR * (Math.cos(a) * N[1] + Math.sin(a) * B[1]),
        center[2] + tubeR * (Math.cos(a) * N[2] + Math.sin(a) * B[2]),
      ]);
    }
    return base;
  }

  // Two tube strands offset by π
  const strandRings: number[][] = [[], []];
  for (let strand = 0; strand < 2; strand++) {
    const offset = strand * Math.PI;
    for (let i = 0; i <= n; i++) {
      const t = (i / n) * totalAngle;
      const y = t * (pitch / TAU) - halfH;
      const center: Vec3 = [r * Math.cos(t + offset), y, r * Math.sin(t + offset)];
      // Tangent via finite difference
      const dt = 0.01;
      const t2 = t + dt;
      const y2 = t2 * (pitch / TAU) - halfH;
      const next: Vec3 = [r * Math.cos(t2 + offset), y2, r * Math.sin(t2 + offset)];
      const tangent = normalize([next[0] - center[0], next[1] - center[1], next[2] - center[2]]);
      const ringBase = addTubeRing(center, tangent);
      strandRings[strand].push(ringBase);

      if (i > 0) {
        const prev = strandRings[strand][i - 1];
        for (let j = 0; j < tubeSeg; j++) {
          const nextJ = (j + 1) % tubeSeg;
          // Ring edges
          edges.push([ringBase + j, ringBase + nextJ]);
          // Longitudinal edges
          edges.push([prev + j, ringBase + j]);
        }
      } else {
        // First ring edges
        for (let j = 0; j < tubeSeg; j++) {
          edges.push([ringBase + j, ringBase + (j + 1) % tubeSeg]);
        }
      }
    }
  }

  // Thin cross-rungs (single line between strand centers)
  const step = Math.max(1, Math.floor(n / rungCount));
  for (let i = 0; i <= n; i += step) {
    // Connect center of ring (use first vertex of each ring as proxy)
    edges.push([strandRings[0][i], strandRings[1][i]]);
  }

  return { vertices, edges, metadata: { name: 'Double Helix', vertexCount: vertices.length, edgeCount: edges.length, faceCount: 0, formula: `${turns} turn, ${rungCount} rungs` } };
}

// ---------------------------------------------------------------------------
// 19b. DNA (Double Helix with base-pair rungs)
// ---------------------------------------------------------------------------

export interface DnaOpts { radius?: number; pitch?: number; turns?: number; segments?: number; basePairs?: number }

export function generateDnaMesh(opts: DnaOpts = {}): WireframeMesh {
  const r = opts.radius ?? 1.2;
  const pitch = opts.pitch ?? 2.8;
  const turns = opts.turns ?? 1.5;
  const n = opts.segments ?? 100;
  const bpCount = opts.basePairs ?? 16;
  const vertices: Vec3[] = [];
  const edges: [number, number][] = [];
  const totalAngle = turns * TAU;
  const halfH = (turns * pitch) / 2;

  // Two backbone strands (thick tube)
  const tubeSeg = 4;
  const tubeR = 0.1;
  const strandRings: number[][] = [[], []];

  function addRing(center: Vec3, tangent: Vec3): number {
    const base = vertices.length;
    const up: Vec3 = Math.abs(tangent[1]) < 0.99 ? [0, 1, 0] : [1, 0, 0];
    const N: Vec3 = normalize([
      tangent[1] * up[2] - tangent[2] * up[1],
      tangent[2] * up[0] - tangent[0] * up[2],
      tangent[0] * up[1] - tangent[1] * up[0],
    ]);
    const B: Vec3 = [
      tangent[1] * N[2] - tangent[2] * N[1],
      tangent[2] * N[0] - tangent[0] * N[2],
      tangent[0] * N[1] - tangent[1] * N[0],
    ];
    for (let j = 0; j < tubeSeg; j++) {
      const a = (j / tubeSeg) * TAU;
      vertices.push([
        center[0] + tubeR * (Math.cos(a) * N[0] + Math.sin(a) * B[0]),
        center[1] + tubeR * (Math.cos(a) * N[1] + Math.sin(a) * B[1]),
        center[2] + tubeR * (Math.cos(a) * N[2] + Math.sin(a) * B[2]),
      ]);
    }
    return base;
  }

  for (let strand = 0; strand < 2; strand++) {
    const offset = strand * Math.PI;
    for (let i = 0; i <= n; i++) {
      const t = (i / n) * totalAngle;
      const y = t * (pitch / TAU) - halfH;
      const center: Vec3 = [r * Math.cos(t + offset), y, r * Math.sin(t + offset)];
      const dt = 0.01;
      const t2 = t + dt;
      const y2 = t2 * (pitch / TAU) - halfH;
      const next: Vec3 = [r * Math.cos(t2 + offset), y2, r * Math.sin(t2 + offset)];
      const tangent = normalize([next[0] - center[0], next[1] - center[1], next[2] - center[2]]);
      const ringBase = addRing(center, tangent);
      strandRings[strand].push(ringBase);

      if (i > 0) {
        const prev = strandRings[strand][i - 1];
        for (let j = 0; j < tubeSeg; j++) {
          edges.push([ringBase + j, ringBase + (j + 1) % tubeSeg]);
          edges.push([prev + j, ringBase + j]);
        }
      } else {
        for (let j = 0; j < tubeSeg; j++) {
          edges.push([ringBase + j, ringBase + (j + 1) % tubeSeg]);
        }
      }
    }
  }

  // Base-pair rungs: two half-rungs meeting at center (A-T / C-G style)
  const step = Math.max(1, Math.floor(n / bpCount));
  for (let i = 0; i <= n; i += step) {
    const t = (i / n) * totalAngle;
    const y = t * (pitch / TAU) - halfH;
    const a: Vec3 = [r * Math.cos(t), y, r * Math.sin(t)];
    const b: Vec3 = [r * Math.cos(t + Math.PI), y, r * Math.sin(t + Math.PI)];
    const mid: Vec3 = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2];

    const aIdx = vertices.length;
    vertices.push(a);
    const midIdx = vertices.length;
    vertices.push(mid);
    const bIdx = vertices.length;
    vertices.push(b);

    // Two half-rungs (representing base pair bond)
    edges.push([aIdx, midIdx]);
    edges.push([midIdx, bIdx]);
  }

  return { vertices, edges, metadata: { name: 'DNA', vertexCount: vertices.length, edgeCount: edges.length, faceCount: 0, formula: `${turns} turns, ${bpCount} base pairs` } };
}

// ---------------------------------------------------------------------------
// 20. Fibonacci Sphere (golden-angle point distribution)
// ---------------------------------------------------------------------------

export interface FibonacciSphereOpts { radius?: number; points?: number }

export function generateFibonacciSphereMesh(opts: FibonacciSphereOpts = {}): WireframeMesh {
  const r = opts.radius ?? 1.8;
  const N = opts.points ?? 100;
  const goldenAngle = Math.PI * (3 - Math.sqrt(5));
  const edges: [number, number][] = [];
  const edgeSet = new Set<string>();

  // Generate points on unit sphere, then scale
  const unitVerts: Vec3[] = [];
  for (let i = 0; i < N; i++) {
    const y = 1 - (2 * i) / (N - 1);
    const rAtY = Math.sqrt(1 - y * y);
    const theta = goldenAngle * i;
    unitVerts.push([rAtY * Math.cos(theta), y, rAtY * Math.sin(theta)]);
  }

  // Convex hull via brute-force triangle test: for points on a sphere,
  // edge (i,j) is a hull edge iff there exists a plane through i,j
  // with all other points on one side. Test candidate triangles instead:
  // for each triple (i,j,k), if all other points are on the same side
  // of the plane through (i,j,k), it's a hull face → add its 3 edges.
  // O(N⁴) but N=100 is fine (~4M inner ops).
  //
  // Optimization: only test triples where all three are mutual near-neighbors.
  // Pre-compute K=10 nearest neighbors to limit candidate triples.
  const K = 10;
  const nn: number[][] = [];
  for (let i = 0; i < N; i++) {
    const dists: { j: number; d2: number }[] = [];
    for (let j = 0; j < N; j++) {
      if (i === j) continue;
      const dx = unitVerts[i][0] - unitVerts[j][0];
      const dy = unitVerts[i][1] - unitVerts[j][1];
      const dz = unitVerts[i][2] - unitVerts[j][2];
      dists.push({ j, d2: dx * dx + dy * dy + dz * dz });
    }
    dists.sort((a, b) => a.d2 - b.d2);
    nn.push(dists.slice(0, K).map((d) => d.j));
  }

  // For each candidate triangle (i, j, k) where j,k are neighbors of i:
  // compute face normal, check all other points are on the same side.
  function cross(a: Vec3, b: Vec3): Vec3 {
    return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
  }
  function dot(a: Vec3, b: Vec3): number {
    return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
  }
  function sub(a: Vec3, b: Vec3): Vec3 {
    return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
  }

  const tested = new Set<string>();
  for (let i = 0; i < N; i++) {
    for (const j of nn[i]) {
      for (const k of nn[i]) {
        if (j >= k) continue;
        const triKey = [i, j, k].sort((a, b) => a - b).join(',');
        if (tested.has(triKey)) continue;
        tested.add(triKey);

        const vi = unitVerts[i], vj = unitVerts[j], vk = unitVerts[k];
        const normal = cross(sub(vj, vi), sub(vk, vi));
        const len2 = dot(normal, normal);
        if (len2 < 1e-12) continue; // degenerate

        // Check all other points are on one side
        const refD = dot(normal, vi);
        let allPos = true, allNeg = true;
        for (let p = 0; p < N; p++) {
          if (p === i || p === j || p === k) continue;
          const d = dot(normal, unitVerts[p]) - refD;
          if (d > 1e-8) allNeg = false;
          if (d < -1e-8) allPos = false;
          if (!allPos && !allNeg) break;
        }

        if (allPos || allNeg) {
          addEdgeUnique(edges, edgeSet, i, j);
          addEdgeUnique(edges, edgeSet, j, k);
          addEdgeUnique(edges, edgeSet, i, k);
        }
      }
    }
  }

  const vertices: Vec3[] = unitVerts.map((v) => scale(v, r));
  const faceCount = Math.round(edges.length / 1.5); // Euler: F ≈ E * 2/3 for closed mesh

  return { vertices, edges, metadata: { name: 'Fibonacci Sphere', vertexCount: N, edgeCount: edges.length, faceCount, formula: `Golden angle · ${N} pts` } };
}

// ---------------------------------------------------------------------------
// 21. Vortex (conical spiral)
// ---------------------------------------------------------------------------

export interface VortexOpts { radius?: number; height?: number; turns?: number; segments?: number }

export function generateVortexMesh(opts: VortexOpts = {}): WireframeMesh {
  const R = opts.radius ?? 1.6;
  const h = opts.height ?? 3;
  const turns = opts.turns ?? 5;
  const n = opts.segments ?? 150;
  const vertices: Vec3[] = [];
  const edges: [number, number][] = [];
  const totalAngle = turns * TAU;

  for (let i = 0; i <= n; i++) {
    const t = i / n;
    const angle = t * totalAngle;
    const r = R * (1 - t); // radius shrinks to apex
    const y = t * h - h / 2;
    vertices.push([r * Math.cos(angle), y, r * Math.sin(angle)]);
    if (i > 0) edges.push([i - 1, i]);
  }

  return { vertices, edges, metadata: { name: 'Vortex', vertexCount: vertices.length, edgeCount: edges.length, faceCount: 0, formula: `Conical spiral · ${turns} turns` } };
}

// ---------------------------------------------------------------------------
// 22. Lorenz Attractor (chaotic trajectory)
// ---------------------------------------------------------------------------

export interface LorenzOpts { radius?: number; steps?: number; dt?: number }

export function generateLorenzMesh(opts: LorenzOpts = {}): WireframeMesh {
  const r = opts.radius ?? 1.8;
  const steps = opts.steps ?? 3000;
  const dt = opts.dt ?? 0.005;
  const sigma = 10, rho = 28, beta = 8 / 3;

  const vertices: Vec3[] = [];
  const edges: [number, number][] = [];
  let x = 0.1, y = 0, z = 0;

  for (let i = 0; i < steps; i++) {
    vertices.push([x, y, z]);
    if (i > 0) edges.push([i - 1, i]);
    const dx = sigma * (y - x);
    const dy = x * (rho - z) - y;
    const dz = x * y - beta * z;
    x += dx * dt;
    y += dy * dt;
    z += dz * dt;
  }

  // Center and scale to fit radius
  let cx = 0, cy = 0, cz = 0;
  for (const v of vertices) { cx += v[0]; cy += v[1]; cz += v[2]; }
  cx /= vertices.length; cy /= vertices.length; cz /= vertices.length;
  let maxDist = 0;
  for (const v of vertices) {
    v[0] -= cx; v[1] -= cy; v[2] -= cz;
    const d = Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]);
    if (d > maxDist) maxDist = d;
  }
  if (maxDist > 0) {
    const s = r / maxDist;
    for (const v of vertices) { v[0] *= s; v[1] *= s; v[2] *= s; }
  }

  return { vertices, edges, metadata: { name: 'Lorenz Attractor', vertexCount: vertices.length, edgeCount: edges.length, faceCount: 0, formula: 'σ=10 ρ=28 β=8/3' } };
}

// ---------------------------------------------------------------------------
// 23. Sierpinski Tetrahedron (fractal)
// ---------------------------------------------------------------------------

export interface SierpinskiOpts { radius?: number; depth?: number }

export function generateSierpinskiMesh(opts: SierpinskiOpts = {}): WireframeMesh {
  const r = opts.radius ?? 1.8;
  const depth = opts.depth ?? 3;
  const vertices: Vec3[] = [];
  const edges: [number, number][] = [];
  const edgeSet = new Set<string>();

  // Base tetrahedron vertices
  const base: Vec3[] = [
    [1, 1, 1], [1, -1, -1], [-1, 1, -1], [-1, -1, 1],
  ].map((v) => scale(normalize(v as Vec3), r));

  function subdivide(verts: Vec3[], d: number): void {
    if (d === 0) {
      const idx = vertices.length;
      vertices.push(...verts);
      for (let i = 0; i < 4; i++)
        for (let j = i + 1; j < 4; j++)
          addEdgeUnique(edges, edgeSet, idx + i, idx + j);
      return;
    }
    const mid = (a: Vec3, b: Vec3): Vec3 => [(a[0]+b[0])/2, (a[1]+b[1])/2, (a[2]+b[2])/2];
    const [a, b, c, d2] = verts;
    subdivide([a, mid(a,b), mid(a,c), mid(a,d2)], d - 1);
    subdivide([b, mid(a,b), mid(b,c), mid(b,d2)], d - 1);
    subdivide([c, mid(a,c), mid(b,c), mid(c,d2)], d - 1);
    subdivide([d2, mid(a,d2), mid(b,d2), mid(c,d2)], d - 1);
  }

  subdivide(base, depth);
  return { vertices, edges, metadata: { name: 'Sierpinski Tetrahedron', vertexCount: vertices.length, edgeCount: edges.length, faceCount: Math.pow(4, depth) * 4, formula: `Depth ${depth} · ${Math.pow(4, depth)} tetrahedra` } };
}

// ---------------------------------------------------------------------------
// 24. Triple Helix (three strands offset 120°)
// ---------------------------------------------------------------------------

export interface TripleHelixOpts { radius?: number; pitch?: number; turns?: number; segments?: number }

export function generateTripleHelixMesh(opts: TripleHelixOpts = {}): WireframeMesh {
  const r = opts.radius ?? 1.2;
  const pitch = opts.pitch ?? 2.5;
  const turns = opts.turns ?? 1.5;
  const n = opts.segments ?? 80;
  const vertices: Vec3[] = [];
  const edges: [number, number][] = [];
  const totalAngle = turns * TAU;
  const halfH = (turns * pitch) / 2;

  for (let strand = 0; strand < 3; strand++) {
    const offset = strand * (TAU / 3);
    const base = vertices.length;
    for (let i = 0; i <= n; i++) {
      const t = (i / n) * totalAngle;
      vertices.push([r * Math.cos(t + offset), t * (pitch / TAU) - halfH, r * Math.sin(t + offset)]);
      if (i > 0) edges.push([base + i - 1, base + i]);
    }
  }

  // Cross-rungs between adjacent strands every ~10 steps
  const strandLen = n + 1;
  const step = Math.max(1, Math.floor(n / 12));
  for (let i = 0; i <= n; i += step) {
    edges.push([i, strandLen + i]);
    edges.push([strandLen + i, 2 * strandLen + i]);
    edges.push([2 * strandLen + i, i]);
  }

  return { vertices, edges, metadata: { name: 'Triple Helix', vertexCount: vertices.length, edgeCount: edges.length, faceCount: 0, formula: `3 strands · ${turns} turns` } };
}

// ---------------------------------------------------------------------------
// 25. Trinity Cube (IVM / FCC octet truss bounded by a cube)
// Bonds run along the six face-diagonal directions (±1,±1,0) — that is FCC
// nearest-neighbour connectivity, i.e. the IVM. It is NOT a diamond lattice,
// despite what this generator's metadata claimed until 2026-08-04; diamond
// bonds along body-diagonal quarter-steps with coordination 4 (see #40).
// ---------------------------------------------------------------------------

export interface TrinityCubeOpts { radius?: number; cells?: number }

export function generateTrinityCubeMesh(opts: TrinityCubeOpts = {}): WireframeMesh {
  const r = opts.radius ?? 1.6;
  const n = opts.cells ?? 3; // 3 lanes per direction
  const vertices: Vec3[] = [];
  const edges: [number, number][] = [];
  const edgeSet = new Set<string>();
  const vertMap = new Map<string, number>();

  // (n+1)³ grid, centered and scaled to fit radius
  for (let i = 0; i <= n; i++) {
    for (let j = 0; j <= n; j++) {
      for (let k = 0; k <= n; k++) {
        const x = (i / n - 0.5) * 2;
        const y = (j / n - 0.5) * 2;
        const z = (k / n - 0.5) * 2;
        vertMap.set(`${i},${j},${k}`, vertices.length);
        vertices.push([x * r / Math.sqrt(3), y * r / Math.sqrt(3), z * r / Math.sqrt(3)]);
      }
    }
  }

  // Connect only along face-diagonal directions (FCC nearest-neighbor)
  // 6 unique directions: (±1,±1,0), (±1,0,±1), (0,±1,±1)
  const dirs: [number, number, number][] = [
    [1, 1, 0], [1, -1, 0], [1, 0, 1], [1, 0, -1], [0, 1, 1], [0, 1, -1],
  ];

  for (let i = 0; i <= n; i++) {
    for (let j = 0; j <= n; j++) {
      for (let k = 0; k <= n; k++) {
        const aIdx = vertMap.get(`${i},${j},${k}`)!;
        for (const [di, dj, dk] of dirs) {
          const ni = i + di, nj = j + dj, nk = k + dk;
          const bIdx = vertMap.get(`${ni},${nj},${nk}`);
          if (bIdx !== undefined) addEdgeUnique(edges, edgeSet, aIdx, bIdx);
        }
      }
    }
  }

  return { vertices, edges, metadata: { name: 'Trinity Cube', vertexCount: vertices.length, edgeCount: edges.length, faceCount: 0, formula: `IVM / FCC octet truss · ${n} lanes × 6 dirs` } };
}

// ---------------------------------------------------------------------------
// 26. Graphite (stacked graphene sheets with AB stacking)
// ---------------------------------------------------------------------------

export interface GraphiteOpts {
  radius?: number;
  /** Number of hexagon rings per sheet */
  rings?: number;
  /** Number of stacked layers */
  layers?: number;
  /** Inter-layer spacing relative to bond length (real graphite ≈ 2.36) */
  layerSpacing?: number;
}

export function generateGraphiteMesh(opts: GraphiteOpts = {}): WireframeMesh {
  const bondLen = opts.radius ?? 0.3;
  const rings = opts.rings ?? 3;
  const layers = Math.max(1, opts.layers ?? 4);
  const spacing = (opts.layerSpacing ?? 2.36) * bondLen;

  const vertices: Vec3[] = [];
  const vertexMap = new Map<string, number>();
  const edgeSet = new Set<string>();
  const edges: [number, number][] = [];

  const sqrt3 = Math.sqrt(3);
  const a1x = sqrt3 * bondLen;
  const a2x = sqrt3 * bondLen / 2;
  const a2y = 1.5 * bondLen;
  const maxDist = (rings + 0.5) * sqrt3 * bondLen;

  // AB stacking offsets: A layers at origin, B layers shifted by (a1/3 + a2/3)
  const bOffsetX = (a1x + a2x) / 3;
  const bOffsetZ = a2y / 3;

  function vertKey(x: number, y: number, z: number): string {
    return `${(+x.toFixed(4)).toFixed(4)},${(+y.toFixed(4)).toFixed(4)},${(+z.toFixed(4)).toFixed(4)}`;
  }

  function getVert(x: number, y: number, z: number): number {
    const key = vertKey(x, y, z);
    const existing = vertexMap.get(key);
    if (existing !== undefined) return existing;
    const idx = vertices.length;
    vertices.push([x, y, z]);
    vertexMap.set(key, idx);
    return idx;
  }

  // Basis atoms within unit cell (sublattice A and B of honeycomb)
  const basis: [number, number][] = [
    [0, 0],
    [sqrt3 * bondLen / 2, bondLen / 2],
  ];

  // Bond directions from sublattice A
  const directions: [number, number][] = [
    [sqrt3 * bondLen / 2, bondLen / 2],
    [-sqrt3 * bondLen / 2, bondLen / 2],
    [0, -bondLen],
  ];

  // Center the stack vertically
  const totalHeight = (layers - 1) * spacing;
  const yOffset = -totalHeight / 2;

  // Track all vertex indices per layer for inter-layer bonding
  const layerVerts: number[][] = [];

  for (let layer = 0; layer < layers; layer++) {
    const y = yOffset + layer * spacing;
    const isB = layer % 2 === 1;
    const ox = isB ? bOffsetX : 0;
    const oz = isB ? bOffsetZ : 0;

    const layerIndices: number[] = [];

    // Place atoms
    for (let i = -rings - 1; i <= rings + 1; i++) {
      for (let j = -rings - 1; j <= rings + 1; j++) {
        for (const [bx, bz] of basis) {
          const x = i * a1x + j * a2x + bx + ox;
          const z = j * a2y + bz + oz;
          if (Math.sqrt(x * x + z * z) > maxDist) continue;
          layerIndices.push(getVert(x, y, z));
        }
      }
    }

    // In-plane bonds (same as graphene, but at height y with offset)
    for (let i = -rings - 1; i <= rings + 1; i++) {
      for (let j = -rings - 1; j <= rings + 1; j++) {
        const ax = i * a1x + j * a2x + basis[0][0] + ox;
        const az = j * a2y + basis[0][1] + oz;
        if (Math.sqrt(ax * ax + az * az) > maxDist) continue;

        const aIdx = vertexMap.get(vertKey(ax, y, az));
        if (aIdx === undefined) continue;

        for (const [dx, dz] of directions) {
          const bx = ax + dx;
          const bz2 = az + dz;
          const bIdx = vertexMap.get(vertKey(bx, y, bz2));
          if (bIdx !== undefined) {
            addEdgeUnique(edges, edgeSet, aIdx, bIdx);
          }
        }
      }
    }

    layerVerts.push(layerIndices);
  }

  // Inter-layer bonds: connect vertically aligned atoms between adjacent layers.
  // In AB-stacked graphite, sublattice-A of one layer sits above sublattice-B
  // of the next. We connect any atom pairs between adjacent layers that are
  // nearly vertically aligned (small XZ distance).
  const xzThreshold2 = bondLen * bondLen * 0.25;

  for (let layer = 0; layer < layers - 1; layer++) {
    for (const idxA of layerVerts[layer]) {
      const va = vertices[idxA];
      for (const idxB of layerVerts[layer + 1]) {
        const vb = vertices[idxB];
        const dx = va[0] - vb[0];
        const dz = va[2] - vb[2];
        if (dx * dx + dz * dz < xzThreshold2) {
          addEdgeUnique(edges, edgeSet, idxA, idxB);
        }
      }
    }
  }

  // Center and scale to fit radius
  const targetR = (rings + 1) * sqrt3 * bondLen;
  let maxD = 0;
  let cx = 0, cy = 0, cz = 0;
  for (const v of vertices) { cx += v[0]; cy += v[1]; cz += v[2]; }
  cx /= vertices.length; cy /= vertices.length; cz /= vertices.length;
  for (const v of vertices) {
    v[0] -= cx; v[1] -= cy; v[2] -= cz;
    const d = Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]);
    if (d > maxD) maxD = d;
  }
  if (maxD > 0) {
    const s = targetR / maxD;
    for (const v of vertices) { v[0] *= s; v[1] *= s; v[2] *= s; }
  }

  return {
    vertices,
    edges,
    metadata: {
      name: 'Graphite',
      vertexCount: vertices.length,
      edgeCount: edges.length,
      faceCount: 0,
      formula: `${layers} graphene layers, AB stacking, rings=${rings}`,
    },
  };
}

// ---------------------------------------------------------------------------
// 27. Hex Grid (honeycomb lattice clipped to hexagonal boundary)
// ---------------------------------------------------------------------------

export interface HexGridOpts {
  radius?: number;
  /** Number of hex rings from center */
  rings?: number;
  /** Slight Z tilt for 3D effect */
  tilt?: number;
}

export function generateHexGridMesh(opts: HexGridOpts = {}): WireframeMesh {
  const bondLen = opts.radius ?? 0.3;
  const rings = opts.rings ?? 4;
  const tilt = opts.tilt ?? 0.04;

  const vertices: Vec3[] = [];
  const vertexMap = new Map<string, number>();
  const edgeSet = new Set<string>();
  const edges: [number, number][] = [];

  const sqrt3 = Math.sqrt(3);

  function vertKey(x: number, y: number): string {
    return `${(+x.toFixed(4)).toFixed(4)},${(+y.toFixed(4)).toFixed(4)}`;
  }

  function getVert(x: number, y: number): number {
    const key = vertKey(x, y);
    const existing = vertexMap.get(key);
    if (existing !== undefined) return existing;
    const idx = vertices.length;
    const dist = Math.sqrt(x * x + y * y);
    vertices.push([x, y, dist * tilt]);
    vertexMap.set(key, idx);
    return idx;
  }

  // Hexagonal boundary test using axial coordinates:
  // A point at (q, r) in axial hex coords is inside a hex of radius R
  // if max(|q|, |r|, |q+r|) <= R
  // We convert cartesian to axial: q = (2x/3) / bondLen, r = (-x/3 + sqrt3*y/3) / bondLen
  // But simpler: use the honeycomb lattice unit cell indices directly.
  // The hex boundary in cartesian: a regular hexagon with circumradius = rings * sqrt3 * bondLen
  // Test: project onto 3 hex axes and check all within bounds.
  const hexR = rings * sqrt3 * bondLen;

  function insideHex(x: number, y: number): boolean {
    // Regular hexagon centered at origin, flat-top orientation
    // Test against 3 pairs of parallel edges
    const ax = Math.abs(x);
    const ay = Math.abs(y);
    // Flat-top hex: width = 2*R, height = sqrt(3)*R
    // Test: |y| <= sqrt(3)/2 * R  AND  |y| + |x|/sqrt(3) <= sqrt(3)/2 * R ... nah
    // Pointy-top hex boundary (matches honeycomb orientation):
    // max(|x|, |x/2 + sqrt3*y/2|, |-x/2 + sqrt3*y/2|) <= R
    const d1 = ax;
    const d2 = Math.abs(x * 0.5 + y * sqrt3 * 0.5);
    const d3 = Math.abs(-x * 0.5 + y * sqrt3 * 0.5);
    return Math.max(d1, d2, d3) <= hexR;
  }

  // Honeycomb lattice vectors
  const a1: [number, number] = [sqrt3 * bondLen, 0];
  const a2: [number, number] = [sqrt3 * bondLen / 2, 1.5 * bondLen];
  const basis: [number, number][] = [
    [0, 0],
    [sqrt3 * bondLen / 2, bondLen / 2],
  ];
  const directions: [number, number][] = [
    [sqrt3 * bondLen / 2, bondLen / 2],
    [-sqrt3 * bondLen / 2, bondLen / 2],
    [0, -bondLen],
  ];

  // Place atoms inside hex boundary
  const extent = rings + 2;
  for (let i = -extent; i <= extent; i++) {
    for (let j = -extent; j <= extent; j++) {
      for (const [bx, by] of basis) {
        const x = i * a1[0] + j * a2[0] + bx;
        const y = i * a1[1] + j * a2[1] + by;
        if (!insideHex(x, y)) continue;
        getVert(x, y);
      }
    }
  }

  // Connect bonds (only between atoms that both exist inside the hex)
  for (let i = -extent; i <= extent; i++) {
    for (let j = -extent; j <= extent; j++) {
      const ax = i * a1[0] + j * a2[0] + basis[0][0];
      const ay = i * a1[1] + j * a2[1] + basis[0][1];
      if (!insideHex(ax, ay)) continue;

      const aIdx = vertexMap.get(vertKey(ax, ay));
      if (aIdx === undefined) continue;

      for (const [dx, dy] of directions) {
        const bx = ax + dx;
        const by = ay + dy;
        const bIdx = vertexMap.get(vertKey(bx, by));
        if (bIdx !== undefined) {
          addEdgeUnique(edges, edgeSet, aIdx, bIdx);
        }
      }
    }
  }

  return {
    vertices,
    edges,
    metadata: {
      name: 'Hex Grid',
      vertexCount: vertices.length,
      edgeCount: edges.length,
      faceCount: Math.floor(edges.length / 3),
      formula: `Hexagonal honeycomb, ${rings} rings`,
    },
  };
}

// ---------------------------------------------------------------------------
// 28. Cubic Sheet (flat square grid with slight 3D tilt)
// ---------------------------------------------------------------------------

export interface CubicSheetOpts {
  radius?: number;
  /** Grid cells per side */
  cells?: number;
  /** Slight Z tilt for 3D effect */
  tilt?: number;
}

export function generateCubicSheetMesh(opts: CubicSheetOpts = {}): WireframeMesh {
  const cellSize = opts.radius ?? 0.35;
  const cells = opts.cells ?? 8;
  const tilt = opts.tilt ?? 0.04;

  const vertices: Vec3[] = [];
  const edges: [number, number][] = [];

  const half = (cells * cellSize) / 2;

  // (cells+1) x (cells+1) grid of vertices
  const grid: number[][] = [];
  for (let i = 0; i <= cells; i++) {
    const row: number[] = [];
    for (let j = 0; j <= cells; j++) {
      const x = i * cellSize - half;
      const y = j * cellSize - half;
      const dist = Math.sqrt(x * x + y * y);
      row.push(vertices.length);
      vertices.push([x, y, dist * tilt]);
    }
    grid.push(row);
  }

  // Horizontal edges
  for (let i = 0; i <= cells; i++) {
    for (let j = 0; j < cells; j++) {
      edges.push([grid[i][j], grid[i][j + 1]]);
    }
  }

  // Vertical edges
  for (let i = 0; i < cells; i++) {
    for (let j = 0; j <= cells; j++) {
      edges.push([grid[i][j], grid[i + 1][j]]);
    }
  }

  return {
    vertices,
    edges,
    metadata: {
      name: 'Cubic Sheet',
      vertexCount: vertices.length,
      edgeCount: edges.length,
      faceCount: cells * cells,
      formula: `${cells}×${cells} square grid`,
    },
  };
}

// ---------------------------------------------------------------------------
// 29. Cubic Lattice (simple cubic 3D grid)
// ---------------------------------------------------------------------------

export interface CubicLatticeOpts {
  radius?: number;
  /** Unit cells per axis */
  cells?: number;
}

export function generateCubicLatticeMesh(opts: CubicLatticeOpts = {}): WireframeMesh {
  const r = opts.radius ?? 1.5;
  const cells = opts.cells ?? 3;

  const vertices: Vec3[] = [];
  const edges: [number, number][] = [];
  const edgeSet = new Set<string>();
  const vertMap = new Map<string, number>();

  // (cells+1)³ grid, centered
  for (let i = 0; i <= cells; i++) {
    for (let j = 0; j <= cells; j++) {
      for (let k = 0; k <= cells; k++) {
        const x = (i / cells - 0.5) * 2;
        const y = (j / cells - 0.5) * 2;
        const z = (k / cells - 0.5) * 2;
        vertMap.set(`${i},${j},${k}`, vertices.length);
        vertices.push([x * r / Math.sqrt(3), y * r / Math.sqrt(3), z * r / Math.sqrt(3)]);
      }
    }
  }

  // Connect along 3 axes: +x, +y, +z
  const dirs: [number, number, number][] = [[1, 0, 0], [0, 1, 0], [0, 0, 1]];
  for (let i = 0; i <= cells; i++) {
    for (let j = 0; j <= cells; j++) {
      for (let k = 0; k <= cells; k++) {
        const aIdx = vertMap.get(`${i},${j},${k}`)!;
        for (const [di, dj, dk] of dirs) {
          const bIdx = vertMap.get(`${i + di},${j + dj},${k + dk}`);
          if (bIdx !== undefined) addEdgeUnique(edges, edgeSet, aIdx, bIdx);
        }
      }
    }
  }

  const n = cells + 1;
  return {
    vertices,
    edges,
    metadata: {
      name: 'Cubic Lattice',
      vertexCount: n * n * n,
      edgeCount: edges.length,
      faceCount: 0,
      formula: `Simple cubic ${cells}³ cells`,
    },
  };
}

// ---------------------------------------------------------------------------
// 31. Boerdijk–Coxeter Tetrahelix
// Helical stack of regular tetrahedra; vertices on a cylindrical helix.
// For unit edge: helix radius r = 3√3/10, axial step h = 1/√10, twist θ = acos(-2/3).
// Each vertex connects to its next 3 successors (the three other corners of the
// face-adjacent tetrahedra it belongs to).
// ---------------------------------------------------------------------------

export interface TetrahelixOpts { radius?: number; segments?: number }

export function generateTetrahelixMesh(opts: TetrahelixOpts = {}): WireframeMesh {
  const R = opts.radius ?? 0.7;
  const n = opts.segments ?? 12;

  const helixR = (3 * Math.sqrt(3)) / 10; // ≈ 0.5196 for unit edge
  const a = R / helixR;
  const h = a / Math.sqrt(10);
  const theta = Math.acos(-2 / 3);

  const totalH = (n - 1) * h;
  const yOff = -totalH / 2;

  const vertices: Vec3[] = [];
  for (let i = 0; i < n; i++) {
    vertices.push([R * Math.cos(i * theta), i * h + yOff, R * Math.sin(i * theta)]);
  }

  const edges: [number, number][] = [];
  for (let i = 0; i < n; i++) {
    for (const d of [1, 2, 3]) {
      if (i + d < n) edges.push([i, i + d]);
    }
  }

  return {
    vertices,
    edges,
    metadata: {
      name: 'Tetrahelix',
      vertexCount: vertices.length,
      edgeCount: edges.length,
      faceCount: Math.max(0, n - 3),
      formula: `Boerdijk–Coxeter · ${Math.max(0, n - 3)} tetrahedra`,
    },
  };
}

// ---------------------------------------------------------------------------
// 32. Metatron's Cube — 13 fruit-of-life centers, all-pairs lines
// Center + inner hex (radius 1) + outer hex (radius √3, offset 30°).
// ---------------------------------------------------------------------------

export interface MetatronCubeOpts { radius?: number }

export function generateMetatronCubeMesh(opts: MetatronCubeOpts = {}): WireframeMesh {
  const R = opts.radius ?? 1.6;
  const k = R / Math.sqrt(3);

  const vertices: Vec3[] = [[0, 0, 0]];
  for (let i = 0; i < 6; i++) {
    const a = (i * Math.PI) / 3;
    vertices.push([k * Math.cos(a), k * Math.sin(a), 0]);
  }
  for (let i = 0; i < 6; i++) {
    const a = Math.PI / 6 + (i * Math.PI) / 3;
    vertices.push([k * Math.sqrt(3) * Math.cos(a), k * Math.sqrt(3) * Math.sin(a), 0]);
  }

  const edges: [number, number][] = [];
  for (let i = 0; i < 13; i++) {
    for (let j = i + 1; j < 13; j++) edges.push([i, j]);
  }

  return {
    vertices,
    edges,
    metadata: {
      name: "Metatron's Cube",
      vertexCount: 13,
      edgeCount: 78,
      faceCount: 0,
      formula: 'All-pairs · fruit-of-life centers',
    },
  };
}

// ---------------------------------------------------------------------------
// 33. Rhombic Dodecahedron — dual of the cuboctahedron.
// 14 vertices = 8 cube corners (±1,±1,±1) ∪ 6 octahedron tips (±2,0,0)…
// Edges connect each cube corner to its 3 nearest octahedron tips (dist √3).
// ---------------------------------------------------------------------------

export interface RhombicDodecahedronOpts { radius?: number }

export function generateRhombicDodecahedronMesh(opts: RhombicDodecahedronOpts = {}): WireframeMesh {
  const r = opts.radius ?? 1.6;
  const s = r / 2;

  const vertices: Vec3[] = [];
  for (const sx of [-1, 1])
    for (const sy of [-1, 1])
      for (const sz of [-1, 1])
        vertices.push([sx * s, sy * s, sz * s]);
  vertices.push([2 * s, 0, 0], [-2 * s, 0, 0]);
  vertices.push([0, 2 * s, 0], [0, -2 * s, 0]);
  vertices.push([0, 0, 2 * s], [0, 0, -2 * s]);

  const edges: [number, number][] = [];
  const target = Math.sqrt(3) * s;
  const tol = 0.01 * s;
  for (let i = 0; i < 8; i++) {
    for (let j = 8; j < 14; j++) {
      const dx = vertices[i][0] - vertices[j][0];
      const dy = vertices[i][1] - vertices[j][1];
      const dz = vertices[i][2] - vertices[j][2];
      const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (Math.abs(d - target) < tol) edges.push([i, j]);
    }
  }

  return {
    vertices,
    edges,
    metadata: {
      name: 'Rhombic Dodecahedron',
      vertexCount: 14,
      edgeCount: edges.length,
      faceCount: 12,
      formula: 'V=14 E=24 F=12 · dual of cuboctahedron',
    },
  };
}

// ---------------------------------------------------------------------------
// 34. Tesseract (4D hypercube)
// 16 vertices in 4D, perspective-projected to 3D from a viewpoint at w = dW.
// Edges connect 4D vertices that differ in exactly one coordinate.
// ---------------------------------------------------------------------------

export interface TesseractOpts { radius?: number; w?: number }

export function generateTesseractMesh(opts: TesseractOpts = {}): WireframeMesh {
  const R = opts.radius ?? 1.4;
  const dW = opts.w ?? 3;

  const v4: [number, number, number, number][] = [];
  for (const sx of [-1, 1])
    for (const sy of [-1, 1])
      for (const sz of [-1, 1])
        for (const sw of [-1, 1])
          v4.push([sx, sy, sz, sw]);

  const vertices: Vec3[] = v4.map(([x, y, z, w]) => {
    const k = R / (dW - w);
    return [x * k, y * k, z * k];
  });

  const edges: [number, number][] = [];
  for (let i = 0; i < 16; i++) {
    for (let j = i + 1; j < 16; j++) {
      let diff = 0;
      for (let c = 0; c < 4; c++) if (v4[i][c] !== v4[j][c]) diff++;
      if (diff === 1) edges.push([i, j]);
    }
  }

  return {
    vertices,
    edges,
    metadata: {
      name: 'Tesseract',
      vertexCount: 16,
      edgeCount: 32,
      faceCount: 24,
      formula: '4D hypercube · perspective projection',
    },
  };
}

// ---------------------------------------------------------------------------
// 35. Borromean Rings — three rings, no two linked, all three together are.
// Three orthogonal rings sharing a common center; visually iconic Trinity form.
// ---------------------------------------------------------------------------

export interface BorromeanRingsOpts { radius?: number; segments?: number }

export function generateBorromeanRingsMesh(opts: BorromeanRingsOpts = {}): WireframeMesh {
  const R = opts.radius ?? 1.4;
  const n = opts.segments ?? 64;

  const vertices: Vec3[] = [];
  const edges: [number, number][] = [];

  const planes: ('xy' | 'yz' | 'zx')[] = ['xy', 'yz', 'zx'];
  for (const plane of planes) {
    const start = vertices.length;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * TAU;
      const c = R * Math.cos(a);
      const s = R * Math.sin(a);
      if (plane === 'xy') vertices.push([c, s, 0]);
      else if (plane === 'yz') vertices.push([0, c, s]);
      else vertices.push([s, 0, c]);
    }
    for (let i = 0; i < n; i++) edges.push([start + i, start + ((i + 1) % n)]);
  }

  return {
    vertices,
    edges,
    metadata: {
      name: 'Borromean Rings',
      vertexCount: vertices.length,
      edgeCount: edges.length,
      faceCount: 3,
      formula: '3 orthogonal rings · Brunnian link',
    },
  };
}

// ---------------------------------------------------------------------------
// 36. Klein Bottle — figure-8 immersion (closed, non-orientable).
// Both u, v wrap simply in this immersion.
// ---------------------------------------------------------------------------

export interface KleinBottleOpts { radius?: number; tubeRadius?: number; uSeg?: number; vSeg?: number }

export function generateKleinBottleMesh(opts: KleinBottleOpts = {}): WireframeMesh {
  const R = opts.radius ?? 1.0;
  const r = opts.tubeRadius ?? 0.32;
  const Nu = opts.uSeg ?? 16;
  const Nv = opts.vSeg ?? 28;

  const vertices: Vec3[] = [];
  for (let i = 0; i < Nu; i++) {
    const u = (i / Nu) * TAU;
    const su = Math.sin(u);
    const s2u = Math.sin(2 * u);
    for (let j = 0; j < Nv; j++) {
      const v = (j / Nv) * TAU;
      const cv = Math.cos(v);
      const sv = Math.sin(v);
      const cv2 = Math.cos(v / 2);
      const sv2 = Math.sin(v / 2);
      const rad = R + r * cv2 * su - r * sv2 * s2u;
      vertices.push([rad * cv, rad * sv, r * sv2 * su + r * cv2 * s2u]);
    }
  }

  const edges: [number, number][] = [];
  for (let i = 0; i < Nu; i++) {
    for (let j = 0; j < Nv; j++) {
      const a = i * Nv + j;
      const b = i * Nv + ((j + 1) % Nv);
      const c = ((i + 1) % Nu) * Nv + j;
      edges.push([a, b], [a, c]);
    }
  }

  return {
    vertices,
    edges,
    metadata: {
      name: 'Klein Bottle',
      vertexCount: vertices.length,
      edgeCount: edges.length,
      faceCount: Nu * Nv,
      formula: 'Figure-8 immersion · non-orientable',
    },
  };
}

// ---------------------------------------------------------------------------
// 37. Diamond Lattice (sp³) — SUPERSEDED by generateDiamondMesh (#40).
//
// @deprecated Geometrically correct but raggedly terminated: a raw box
// truncation leaves dangling single-bond stubs (10 of them at cells=2, with
// only 42% of atoms showing the full tetrahedron). #40 erodes those away and
// returns a single connected fragment. Kept because this module is a public
// subpath export; prefer generateDiamondMesh for anything new.
//
// Two interpenetrating FCC lattices, offset by (¼,¼,¼). Each atom bonds to its
// 4 nearest neighbours at distance √3/4 of the conventional cell edge.
// ---------------------------------------------------------------------------

export interface DiamondLatticeOpts { radius?: number; cells?: number }

export function generateDiamondLatticeMesh(opts: DiamondLatticeOpts = {}): WireframeMesh {
  const R = opts.radius ?? 1.6;
  const cells = opts.cells ?? 2;

  const fccBase: Vec3[] = [
    [0, 0, 0], [0.5, 0.5, 0], [0.5, 0, 0.5], [0, 0.5, 0.5],
  ];
  const sub2: Vec3[] = fccBase.map(([x, y, z]) => [x + 0.25, y + 0.25, z + 0.25]);
  const cellAtoms = [...fccBase, ...sub2];

  const vertices: Vec3[] = [];
  const vertMap = new Map<string, number>();
  const key = (x: number, y: number, z: number): string =>
    `${Math.round(x * 1e6)},${Math.round(y * 1e6)},${Math.round(z * 1e6)}`;

  for (let i = 0; i < cells; i++) {
    for (let j = 0; j < cells; j++) {
      for (let k = 0; k < cells; k++) {
        for (const [ax, ay, az] of cellAtoms) {
          const x = i + ax;
          const y = j + ay;
          const z = k + az;
          const ks = key(x, y, z);
          if (!vertMap.has(ks)) {
            vertMap.set(ks, vertices.length);
            vertices.push([x, y, z]);
          }
        }
      }
    }
  }

  const half = cells / 2;
  let maxDim = 0;
  for (const v of vertices) {
    v[0] -= half;
    v[1] -= half;
    v[2] -= half;
    const m = Math.max(Math.abs(v[0]), Math.abs(v[1]), Math.abs(v[2]));
    if (m > maxDim) maxDim = m;
  }
  const sFit = maxDim > 0 ? R / maxDim : 1;
  for (const v of vertices) {
    v[0] *= sFit;
    v[1] *= sFit;
    v[2] *= sFit;
  }

  const bondTarget = (Math.sqrt(3) / 4) * sFit;
  const tol = bondTarget * 0.05;
  const edges: [number, number][] = [];
  const edgeSet = new Set<string>();
  for (let a = 0; a < vertices.length; a++) {
    for (let b = a + 1; b < vertices.length; b++) {
      const dx = vertices[a][0] - vertices[b][0];
      const dy = vertices[a][1] - vertices[b][1];
      const dz = vertices[a][2] - vertices[b][2];
      const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (Math.abs(d - bondTarget) < tol) addEdgeUnique(edges, edgeSet, a, b);
    }
  }

  return {
    vertices,
    edges,
    metadata: {
      name: 'Diamond Lattice',
      vertexCount: vertices.length,
      edgeCount: edges.length,
      faceCount: 0,
      formula: `sp³ tetrahedral · ${cells}³ cells`,
    },
  };
}

// ---------------------------------------------------------------------------
// 38. Rössler Attractor — companion to Lorenz, single-loop "ribbon".
// Standard parameters a=0.2, b=0.2, c=5.7.
// ---------------------------------------------------------------------------

export interface RosslerOpts { radius?: number; steps?: number; dt?: number }

export function generateRosslerMesh(opts: RosslerOpts = {}): WireframeMesh {
  const R = opts.radius ?? 1.8;
  const steps = opts.steps ?? 4000;
  const dt = opts.dt ?? 0.02;
  const a = 0.2;
  const b = 0.2;
  const c = 5.7;

  const vertices: Vec3[] = [];
  const edges: [number, number][] = [];
  let x = 1;
  let y = 1;
  let z = 0;

  for (let i = 0; i < steps; i++) {
    vertices.push([x, y, z]);
    if (i > 0) edges.push([i - 1, i]);
    const dx = -y - z;
    const dy = x + a * y;
    const dz = b + z * (x - c);
    x += dx * dt;
    y += dy * dt;
    z += dz * dt;
  }

  let cx = 0;
  let cy = 0;
  let cz = 0;
  for (const v of vertices) {
    cx += v[0];
    cy += v[1];
    cz += v[2];
  }
  cx /= vertices.length;
  cy /= vertices.length;
  cz /= vertices.length;
  let maxDist = 0;
  for (const v of vertices) {
    v[0] -= cx;
    v[1] -= cy;
    v[2] -= cz;
    const d = Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]);
    if (d > maxDist) maxDist = d;
  }
  if (maxDist > 0) {
    const sf = R / maxDist;
    for (const v of vertices) {
      v[0] *= sf;
      v[1] *= sf;
      v[2] *= sf;
    }
  }

  return {
    vertices,
    edges,
    metadata: {
      name: 'Rossler Attractor',
      vertexCount: vertices.length,
      edgeCount: edges.length,
      faceCount: 0,
      formula: 'a=0.2 b=0.2 c=5.7',
    },
  };
}

// ---------------------------------------------------------------------------
// 39. Hyperboloid of One Sheet (ruled surface, "cooling tower")
// Two end-circles connected by two families of straight rulings that cross
// at the waist.
// ---------------------------------------------------------------------------

export interface HyperboloidOpts { radius?: number; height?: number; segments?: number; skew?: number }

export function generateHyperboloidMesh(opts: HyperboloidOpts = {}): WireframeMesh {
  const R = opts.radius ?? 1.3;
  const H = opts.height ?? 2.6;
  const n = opts.segments ?? 28;
  const skew = opts.skew ?? 4;

  const vertices: Vec3[] = [];
  for (let i = 0; i < n; i++) {
    const a = (i / n) * TAU;
    vertices.push([R * Math.cos(a), -H / 2, R * Math.sin(a)]);
  }
  for (let i = 0; i < n; i++) {
    const a = (i / n) * TAU;
    vertices.push([R * Math.cos(a), H / 2, R * Math.sin(a)]);
  }

  const edges: [number, number][] = [];
  for (let i = 0; i < n; i++) {
    edges.push([i, (i + 1) % n]);
    edges.push([n + i, n + ((i + 1) % n)]);
    edges.push([i, n + ((i + skew) % n)]);
    edges.push([i, n + ((i - skew + n) % n)]);
  }

  return {
    vertices,
    edges,
    metadata: {
      name: 'Hyperboloid',
      vertexCount: vertices.length,
      edgeCount: edges.length,
      faceCount: 0,
      formula: 'Ruled surface · two line families',
    },
  };
}

// ---------------------------------------------------------------------------
// 40. Diamond — the diamond-cubic carbon lattice (sp³)
//
// Two interpenetrating FCC sublattices offset by (¼,¼,¼) along the body
// diagonal. Every interior atom bonds to exactly four neighbours at the
// tetrahedral angle arccos(−⅓) = 109.471°, and the shortest closed circuit in
// the network is a six-membered ring in chair conformation.
//
// This is NOT the IVM (#9). The IVM is FCC nearest-neighbour packing:
// coordination 12, and its shortest rings are triangles. Diamond contains no
// triangle anywhere. Any figure labelled "diamond" must use THIS generator —
// rendering an IVM under a diamond caption is the error this exists to end.
//
// The arithmetic is exact. Working in quarter-cell integer units, a site
// belongs to the lattice iff its coordinates share a parity AND their sum is
// right: all even with (x+y+z) ≡ 0 (mod 4) is the first FCC sublattice, all
// odd with ≡ 3 (mod 4) is the second. Both halves matter — the sum test alone
// would also admit mixed-parity points such as (1,1,2), which are octahedral
// interstices, not lattice sites, and they arrive as unbonded dust. Each
// sublattice has four fixed bond offsets, so bonding needs no distance search
// and no floating-point tolerance.
//
// A raw box truncation leaves ragged single-bond stubs — the older
// generateDiamondLatticeMesh leaves 10 of them at cells=2, and only 42% of its
// atoms show the tetrahedron at all. Here the fragment is eroded after
// bonding: atoms carrying fewer than `minBonds` bonds are dropped repeatedly
// until the set is stable, so the surface terminates on real lattice
// directions instead of spikes.
// ---------------------------------------------------------------------------

export interface DiamondOpts {
  radius?: number;
  /** Conventional cubic cells per axis (default 2) */
  cells?: number;
  /**
   * Erode atoms below this bond count until stable (default 2; 0 disables).
   * Clamped to 0..2: every surface atom of a finite fragment has fewer than
   * three bonds, so a threshold of 3 cascades inward and consumes the whole
   * crystal.
   */
  minBonds?: number;
}

export function generateDiamondMesh(opts: DiamondOpts = {}): WireframeMesh {
  const R = opts.radius ?? 1.6;
  const cells = Math.max(1, Math.round(opts.cells ?? 2));
  const minBonds = Math.max(0, Math.min(2, Math.round(opts.minBonds ?? 2)));

  const span = cells * 4; // quarter-cell units, inclusive of the far face
  const mod4 = (n: number): number => ((n % 4) + 4) % 4;

  // Sublattice A ≡ 0 steps along the four body-diagonal quarter-vectors;
  // sublattice B ≡ 3 steps along their negatives.
  const BONDS_A: Vec3[] = [[1, 1, 1], [1, -1, -1], [-1, 1, -1], [-1, -1, 1]];
  const BONDS_B: Vec3[] = [[-1, -1, -1], [-1, 1, 1], [1, -1, 1], [1, 1, -1]];

  const key = (x: number, y: number, z: number): string => `${x},${y},${z}`;
  // Sublattice A is the all-even one, B the all-odd one.
  const isA = (p: Vec3): boolean => p[0] % 2 === 0;
  const neighboursOf = (p: Vec3): Vec3[] =>
    (isA(p) ? BONDS_A : BONDS_B).map(
      ([dx, dy, dz]) => [p[0] + dx, p[1] + dy, p[2] + dz] as Vec3,
    );

  const sites = new Map<string, Vec3>();
  for (let x = 0; x <= span; x++) {
    for (let y = 0; y <= span; y++) {
      for (let z = 0; z <= span; z++) {
        const even = x % 2 === 0 && y % 2 === 0 && z % 2 === 0;
        const odd = x % 2 !== 0 && y % 2 !== 0 && z % 2 !== 0;
        const m = mod4(x + y + z);
        if ((even && m === 0) || (odd && m === 3)) sites.set(key(x, y, z), [x, y, z]);
      }
    }
  }

  // Erode until stable. Keep the last non-empty generation: on a small
  // fragment an aggressive minBonds could otherwise consume everything.
  if (minBonds > 0) {
    for (;;) {
      const doomed: string[] = [];
      for (const [k, p] of sites) {
        let n = 0;
        for (const q of neighboursOf(p)) {
          if (sites.has(key(q[0], q[1], q[2]))) n++;
        }
        if (n < minBonds) doomed.push(k);
      }
      if (doomed.length === 0 || doomed.length === sites.size) break;
      for (const k of doomed) sites.delete(k);
    }
  }

  const vertices: Vec3[] = [];
  const index = new Map<string, number>();
  for (const [k, p] of sites) {
    index.set(k, vertices.length);
    vertices.push([p[0], p[1], p[2]]);
  }

  const edges: [number, number][] = [];
  const edgeSet = new Set<string>();
  for (const [k, p] of sites) {
    const a = index.get(k)!;
    for (const q of neighboursOf(p)) {
      const b = index.get(key(q[0], q[1], q[2]));
      if (b !== undefined) addEdgeUnique(edges, edgeSet, a, b);
    }
  }

  // Centre on the bounding box, then fit that box to the radius so the
  // fragment fills the frame squarely.
  if (vertices.length > 0) {
    let lo: Vec3 = [Infinity, Infinity, Infinity];
    let hi: Vec3 = [-Infinity, -Infinity, -Infinity];
    for (const v of vertices) {
      for (let d = 0; d < 3; d++) {
        if (v[d] < lo[d]) lo[d] = v[d];
        if (v[d] > hi[d]) hi[d] = v[d];
      }
    }
    let maxDim = 0;
    for (const v of vertices) {
      for (let d = 0; d < 3; d++) {
        v[d] -= (lo[d] + hi[d]) / 2;
        const m = Math.abs(v[d]);
        if (m > maxDim) maxDim = m;
      }
    }
    if (maxDim > 0) {
      const s = R / maxDim;
      for (const v of vertices) {
        v[0] *= s;
        v[1] *= s;
        v[2] *= s;
      }
    }
  }

  return {
    vertices,
    edges,
    metadata: {
      name: 'Diamond',
      vertexCount: vertices.length,
      edgeCount: edges.length,
      faceCount: 0,
      formula: `sp³ · 109.471° · ${cells}³ cells`,
    },
  };
}
